import { IObservableValue, ObservableValue } from "azure-devops-ui/Core/Observable";
import React from "react";
import { deleteCookie, getCookie, getCookies } from "../../common/utilities/browser";
import { IMessage } from "../types/message";
import { IGraphProfile } from "../types/profile";

import avatarImageUrl from "../../public/static/media/avatar.png";

declare const window: Window &
  typeof globalThis & {
    SessionInformation?: ISessionInformation;
    cookieStore?: EventTarget & { get: (cookieName: string) => Promise<{ value: string } | null> };
  };

export const accessTokenName = "AccessToken";
export const idTokenName = "IdToken";
export const refreshTokenName = "RefreshToken";
export const approvedScopes = ["OneDrive.ReadWrite", "User.Read", "Files.ReadWrite", "People.Read"];

export const msaTenantId = "9188040d-6c67-4c5b-b112-36a304b66dad";

/**
 * The session information is data that comes from the server when the page is
 * generated. This data is merged with run-time state to make up the
 * SessionContext.
 */
export interface ISessionInformation {
  ariaToken?: string;
  cdnDomain?: string;
  clientId: string;
  clientVersion: string;
  dogfoodUser: boolean;
  embedded?: boolean;
  environment: string;
  experiments: { [name: string]: any };
  loginAuthority: string;
  message?: IMessage;
  pathname?: string;
  ramps: { [name: string]: boolean };
  redirectUri: string;
  ring: string;
  serverVersion: string;
  tenantId?: string;
  tenantType?: string;
  userId?: string;
  userProfile?: IUserProfile;
  telemetryEndpoint?: string;
}

export interface ISessionContext extends ISessionInformation {
  accountType?: "business" | "consumer";
  ariaToken?: string;
  authenticated: () => boolean;
  driveId: string;
  getPage: () => { current: string; previous: string };
  graphProfile: IObservableValue<IGraphProfile | undefined>;
  locale: string;
  migrated?: boolean;
  profileUrl: IObservableValue<string>;
  session?: string;
  setCurrentPage: (page: string) => void;
  standalone: boolean | undefined;
}

export interface IUserProfile {
  family_name?: string;
  given_name?: string;
  name: string;
  locale: string;
  oid: string;
  preferred_username: string;
}

export function getSessionContext(): ISessionContext {
  // We will initialize the configuration with the development configuration.
  let configuration: ISessionInformation = {
    ariaToken: "b099dca11fed49d7994ca255014628ad-64f54736-b2a7-44a6-9c34-4348f6d52011-7189",
    cdnDomain: "onedrvphotosp-cdn.azureedge.net",
    clientId: "cac44c57-87e7-4c86-88e2-e2ea658ecb30",
    clientVersion: "dev",
    dogfoodUser: true,
    environment: "development",
    experiments: {},
    loginAuthority: "login.microsoftonline.com",
    ramps: {},
    redirectUri: "http://localhost:3000/auth/login",
    ring: "dev",
    serverVersion: "dev",
    telemetryEndpoint: "https://browser.events.data.microsoft.com/OneCollector/1.0/"
  };

  if (window.SessionInformation) {
    configuration = window.SessionInformation;
  }

  // Determine if this session is running in standalone mode (PWA).
  const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)")?.matches;

  let currentPage = "";
  let previousPage = "";
  let tenantId: string | undefined;
  let userId: string | undefined;
  let userProfile = configuration.userProfile;

  // We only process the DevMode_UserProfile cookie in a development build.
  // istanbul ignore next - we always test in "test" environment, we cover all code.
  if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") {
    const _userProfile = getCookie("DevMode_UserProfile");

    try {
      if (_userProfile) {
        const devModeProfile = JSON.parse(decodeURIComponent(_userProfile)) as {
          tenantId?: string;
          userId?: string;
          userProfile: IUserProfile;
        };

        // Update our session information with details parsed from the devmode userprofile.
        tenantId = devModeProfile.tenantId;
        userId = devModeProfile.userId;
        userProfile = devModeProfile.userProfile;
      }
    } catch {
      document.cookie.split(";").forEach(function (c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString());
      });

      window.location.pathname = "/";
    }
  }

  // Cache the refreshToken when the cookieStore is available.
  let refreshToken: string | undefined;
  if (window.cookieStore) {
    refreshToken = getCookie("RefreshToken");
    window.cookieStore.addEventListener("change", () => {
      window.cookieStore!.get("RefreshToken").then((cookie) => (refreshToken = cookie?.value));
    });
  }

  // Create the initial default session context from the page state.
  return {
    accountType: (tenantId || configuration.tenantId) === msaTenantId ? "consumer" : "business",
    authenticated: () => {
      // If your embedded return true, the host will have to autheticate the client.
      if (configuration.embedded) {
        return true;
      }

      // If there is a userProfile and a refresh token available the client is authenticated
      if (userProfile && (refreshToken || getCookie("RefreshToken"))) {
        return true;
      }

      return false;
    },
    driveId: "me",
    locale: userProfile?.locale || "en-us",
    getPage: () => ({ current: currentPage || window.location.pathname, previous: previousPage }),
    graphProfile: new ObservableValue<IGraphProfile | undefined>(undefined),
    migrated: false,
    profileUrl: new ObservableValue(avatarImageUrl),
    session: getCookie("session"),
    setCurrentPage: (page: string) => {
      previousPage = currentPage;
      currentPage = page;
    },
    standalone,
    tenantId,
    tenantType: (tenantId || configuration.tenantId) === msaTenantId ? "msa" : "aad",
    userId,
    userProfile,
    ...configuration
  };
}

export function getSignInUri(sessionContext: ISessionContext, site: string, loginHint?: string): string {
  // Remove the login hint from the redirect URI, we will accept whatever user
  // ends up logging in. If we dont we could end up in a login loop.
  const currentParams = new URLSearchParams(window.location.search);
  currentParams.delete("login_hint");

  // We are using the state parameter as the target page to land on after the login.
  const currentParamsValue = currentParams.toString();
  const state = currentParamsValue ? `${window.location.pathname}?${currentParamsValue}` : window.location.pathname;

  // Build up the set of parameters we will pass to the login authority.
  const authorization: { [param: string]: string } = {
    client_id: sessionContext.clientId,
    nonce: `${site}.${sessionContext.session || "53bebd0c-4d8a-4448-9991-e0f9dbc4a83a"}`,
    redirect_uri: sessionContext.redirectUri,
    response_mode: "form_post",
    response_type: "code",
    scope: "OneDrive.ReadWrite Files.ReadWrite User.Read offline_access openid profile",
    state
  };

  // Add the login hint if one was supplied
  if (loginHint) {
    authorization.login_hint = loginHint;
  }

  return new URL(
    `https://${sessionContext.loginAuthority}/consumers/oauth2/v2.0/authorize?${new URLSearchParams(authorization).toString()}`
  ).toString();
}

/**
 * Removes the cookies that are associated to the user to log them out
 */
export function clearUserCookies() {
  const cookies = getCookies();

  // Delete all accesstoken cookies.
  for (let cookieName in cookies) {
    if (cookieName.indexOf("AccessToken-") === 0) {
      deleteCookie(cookieName);
    }
  }

  deleteCookie(idTokenName);
  deleteCookie(refreshTokenName);
}

export const SessionContext = React.createContext<ISessionContext>(getSessionContext());

// Clear out any global server configuration. This should be accessed via the
// SessionContext, not via a global variable.
window.SessionInformation = undefined;
